import React, {useContext} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getAllFilterKeys} from 'components/Filters/utils'
import {HotelCardContext} from 'components/HotelCardContext'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getHotel} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import {prop} from 'ramda'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {getMatchingFilters} from './getMatchingFilters'

const ScrollWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s200};
  white-space: nowrap;
  overflow: scroll;
  padding: ${cssTheme.layout.spacing.s400};
  &.--basic {
    padding: ${cssTheme.layout.spacing.s400} 0 0;
  }
  /* hide scrollbar */
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }
  /* remove last separator */
  > span:last-child {
    display: none;
  }
  ${linariaMq.desktopXs} {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    height: 100%;
    padding: 0;
  }
`

const PartialFiltersDivider = styled(Divider)`
  &.--padding-top {
    padding-top: ${cssTheme.layout.spacing.s200};
  }
`

const FilterSpan = styled(Text)`
  &.--strike-through {
    text-decoration: line-through;
  }
  ${linariaMq.desktopXs} {
    padding: 0 ${cssTheme.layout.spacing.s200};
  }
`

const MiddleDot = () => <span>·</span>

const allFilterKeys = getAllFilterKeys()

interface Props {
  hasBasicPropertyDetails: boolean
  isBasic?: boolean
}
const PartialFiltersMatchComponent: React.FC<Props> = ({
  hasBasicPropertyDetails,
  isBasic
}) => {
  const {getFilters, getAppliedStarsLabel} = useFiltersForm(allFilterKeys)
  const listOfValues = useSelector(getListOfValues)
  const {formatMessage} = useIntl()
  const {hotelId} = useContext(HotelCardContext)
  const {isMobile} = useDeviceLayout()
  const {getTotalPriceFilter} = usePriceFilter()
  const currencyCode = useSelector(getCurrencyCode)
  const hotel = useSelector(state => getHotel(state, hotelId))
  const filters = getFilters()

  if (!hotel || !listOfValues) {
    return null
  }

  const enabledCurrencies = getEnabledCurrencies(
    process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
  )
  const currency = enabledCurrencies.find(
    currency => currency.code === currencyCode
  )
  const currencySymbol = prop('symbol', currency)

  const starRatingLabel = getAppliedStarsLabel(filters?.starRatings)

  const {matchedFilters, unmatchedFilters} = getMatchingFilters(
    filters,
    hotel,
    listOfValues,
    formatMessage,
    starRatingLabel,
    currencySymbol,
    getTotalPriceFilter
  )

  if (!unmatchedFilters.length) {
    return null
  }

  return (
    <>
      {isMobile && !isBasic && (
        <PartialFiltersDivider
          className={cx(hasBasicPropertyDetails && '--padding-top')}
        />
      )}
      <ScrollWrapper className={cx(isBasic && '--basic')}>
        <Text colorPath="content.neutral.secondary" variant="bodyXS" tag="span">
          <FormattedMessageWrapper
            id="yourFilters"
            defaultMessage="Your filters"
          />
          :
        </Text>
        {matchedFilters.map(filter => (
          <>
            <FilterSpan
              tag="span"
              colorPath="content.neutral.primary"
              variant="labelXS"
              key={filter}
            >
              {filter}
            </FilterSpan>
            <MiddleDot />
          </>
        ))}
        {unmatchedFilters.map(filter => (
          <>
            <FilterSpan
              className={'--strike-through'}
              colorPath="content.neutral.disabled"
              variant="bodyXS"
              key={filter}
              tag="span"
            >
              {filter}
            </FilterSpan>
            <MiddleDot />
          </>
        ))}
      </ScrollWrapper>
    </>
  )
}

export const PartialFiltersMatch = React.memo(PartialFiltersMatchComponent)
